import {
  ReportTestFilter,
  ReportTestItem,
  ReportTestPage,
  ReportTestStatusFilter,
} from '../../dashboard/ci-mapping/ci-mapping.model';

export type ReportTestChip = 'failed' | 'all' | 'unmatched' | 'matched';

export type ReportTestChipCounts = Record<ReportTestChip, number>;

export interface ReportTestFilterQuery {
  filter: ReportTestFilter;
  status: ReportTestStatusFilter | null;
  q: string | null;
}

export const REPORT_TEST_CHIPS: readonly ReportTestChip[] = ['failed', 'all', 'unmatched', 'matched'];

export const REPORT_TEST_SEARCH_MAX = 200;

export const REPORT_TEST_SEARCH_DELAY_MS = 300;

const EMPTY_KEYS: Partial<Record<ReportTestChip, string>> = {
  failed: 'reportTestTable.emptyFailed',
  unmatched: 'reportTestTable.emptyUnmatched',
  matched: 'reportTestTable.emptyMatched',
};

export function reportTestChipFromAddress(value: string | null | undefined): ReportTestChip | null {
  return value === 'unmatched' ? 'unmatched' : null;
}

export function reportTestChipToPreselect(page: ReportTestPage): ReportTestChip {
  return page.statusCounts.failed > 0 ? 'failed' : 'all';
}

export function reportTestChipCounts(
  source: Pick<ReportTestPage, 'statusCounts' | 'total' | 'matched' | 'unmatched'>,
): ReportTestChipCounts {
  return {
    failed: source.statusCounts.failed,
    all: source.total,
    unmatched: source.unmatched,
    matched: source.matched,
  };
}

export function reportTestFilterQuery(chip: ReportTestChip | null, search: string): ReportTestFilterQuery {
  const active = chip ?? 'all';
  return {
    filter: active === 'failed' ? 'all' : active,
    status: active === 'failed' ? 'failed' : null,
    q: search || null,
  };
}

export function reportTestSearchTerm(raw: string): string {
  return raw.trim().slice(0, REPORT_TEST_SEARCH_MAX);
}

export function reportTestFilterEmptyKey(
  counts: ReportTestChipCounts | null,
  chip: ReportTestChip | null,
  search: string,
): string | null {
  if (!counts || counts.all === 0) return null;
  if (search) return 'reportTestTable.emptySearch';
  return (chip && EMPTY_KEYS[chip]) ?? null;
}

export function reportTestCountKey(count: number): string {
  return count === 1 ? 'reportTestTable.filter.countOne' : 'reportTestTable.filter.countOther';
}

type ReportTestFilterRow = Pick<ReportTestItem, 'name' | 'matchKey' | 'status' | 'testCaseKey'>;

const CHIP_MATCHES: Record<ReportTestChip, (row: ReportTestFilterRow) => boolean> = {
  failed: (row) => row.status === 'FAILED',
  all: () => true,
  unmatched: (row) => row.testCaseKey == null,
  matched: (row) => row.testCaseKey != null,
};

/** Bildet die Suche des Servers nach: Teilstring in Name oder Match-Key, ohne Gross-/Kleinschreibung. */
export function reportTestMatchesFilter(
  row: ReportTestFilterRow,
  chip: ReportTestChip | null,
  search: string,
): boolean {
  if (!CHIP_MATCHES[chip ?? 'all'](row)) return false;
  const term = reportTestSearchTerm(search).toLowerCase();
  return !term || row.name.toLowerCase().includes(term) || row.matchKey.toLowerCase().includes(term);
}
